import type { ProjectFileAffordanceTarget } from "../../../common/files/projectFileAffordances";

/**
 * De dónde baja el visor el PDF que revisa: la ruta de descarga
 * /<entityName>.<fieldName>/<instanceId>/<fileName>. El mapa de campos viaja
 * adjunto al PDF, así que el mismo visor sirve para cualquier documento
 * generado, esté o no en un snapshot.
 */
export type ContractFieldReviewSource = {
  entityName: string;
  fieldName: string;
  instanceId: string;
  fileName: string;
  /** El nombre que ve el usuario, en el título del visor. */
  title: string;
};

/** Los documentos generados (contratos y DJCT) llevan su PDF en este campo. */
const DOCUMENT_PDF_FIELD = "finalDocumentPdf";

const isPdf = (mimetype: string | null | undefined, name: string): boolean =>
  (mimetype ?? "").toLowerCase() === "application/pdf" ||
  (!mimetype && name.toLowerCase().endsWith(".pdf"));

/**
 * El PDF de un documento que se puede revisar, a partir de la celda de archivo
 * donde va el icono «Revisar campos». Solo los documentos generados tienen
 * mapa de campos:
 * - en la edición, el PDF guardado de un documento (EditionDocumentation): no
 *   en una fila nueva o marcada para borrar, ni con el archivo cambiado sin
 *   guardar (la descarga traería el guardado, no el que nombra la celda);
 * - en la edición firmada, el PDF de un documento del snapshot, que se baja
 *   por el snapshot (campo data, uuid del archivo).
 */
export function contractFieldReviewSourceFor(
  target: ProjectFileAffordanceTarget,
): ContractFieldReviewSource | null {
  const { download, row, fileFieldName, filename } = target;
  if (fileFieldName !== DOCUMENT_PDF_FIELD) return null;
  if (!filename || !isPdf(target.mimetype, filename)) return null;

  if (target.snapshotEntityName) {
    if (target.snapshotEntityName !== "SignedEdition") return null;
    if (
      download.entityName !== target.snapshotEntityName ||
      download.fieldName !== "data"
    ) {
      return null;
    }
  } else {
    if (target.entityName !== "EditionDocumentation") return null;
    const status = String(row?.__status__ ?? "original");
    if (status.startsWith("added") || status.endsWith("deleted")) return null;
    if (row?.[`${fileFieldName}_data`]?.name !== filename) return null;
  }

  return {
    entityName: download.entityName,
    fieldName: download.fieldName,
    instanceId: download.instanceId,
    fileName: download.fileName,
    title: filename,
  };
}
